import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Buildings, House, CalendarBlank, Broom, Wrench, ChartBar, SignOut, List, X, Star, ChatCircleText, CreditCard } from '@phosphor-icons/react';

const NAV = [
  { to: '/dashboard', icon: ChartBar, label: 'Dashboard', end: true },
  { to: '/properties', icon: House, label: 'Properties' },
  { to: '/calendar', icon: CalendarBlank, label: 'Calendar' },
  { to: '/bookings', icon: CalendarBlank, label: 'Bookings' },
  { to: '/messaging', icon: ChatCircleText, label: 'Messaging' },
  { to: '/cleaning', icon: Broom, label: 'Cleaning' },
  { to: '/maintenance', icon: Wrench, label: 'Maintenance' },
  { to: '/reviews', icon: Star, label: 'Reviews' },
  { to: '/billing', icon: CreditCard, label: 'Billing' },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const SidebarContent = () => (
    <>
      <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
        <div className="w-7 h-7 bg-[#0052FF] rounded-sm flex items-center justify-center">
          <Buildings weight="bold" className="text-white" size={14} />
        </div>
        <span className="text-sm font-black tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>HostCockpit</span>
      </div>
      <nav className="flex-1 py-2">
        {NAV.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 mx-2 my-0.5 text-sm font-medium rounded-sm transition-colors ${isActive ? 'bg-zinc-100 text-zinc-950 font-bold' : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900'}`}
            data-testid={`sidebar-${item.label.toLowerCase()}`}
          >
            <item.icon size={18} weight="duotone" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-zinc-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center text-xs font-bold text-zinc-600">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-zinc-900 truncate">{user?.name || 'User'}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-red-600 transition-colors w-full" data-testid="sidebar-logout">
          <SignOut size={16} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-zinc-50 flex" data-testid="dashboard-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-56 bg-white border-r border-zinc-200 flex-col fixed inset-y-0 left-0 z-30" data-testid="sidebar">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-56 bg-white flex flex-col h-full">
            <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600">
              <X size={18} />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 lg:ml-56">
        <header className="h-14 bg-white border-b border-zinc-200 flex items-center justify-between px-6 sticky top-0 z-20">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-zinc-600" data-testid="mobile-sidebar-toggle">
            <List size={20} />
          </button>
          <div />
          <div className="text-xs text-zinc-500 font-medium" style={{ fontFamily: 'var(--font-body)' }}>
            Plan: <span className="text-[#0052FF] font-bold uppercase">{user?.subscription_plan || 'Starter'}</span>
          </div>
        </header>
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
